import React, { useState } from "react";
import { Box, Card, Chip, Stack, Typography, useTheme } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { formatDateWithTime } from "utils/formatter";
import { Order } from "types/Order";
import { SectorOrderModal } from "./ModalContent/SectorOrderModal";
import { ActiveOrderDetails } from "./ActiveOrderDetails";

type OrderItemProps = {
  order: Order;
};

export const CardActiveOrders: React.FC<OrderItemProps> = ({ order }) => {
  const { palette } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpen = () => setIsModalOpen(true);
  const handleClose = () => setIsModalOpen(false);

  /* ---------- status helpers ---------- */
  const isCanceled = order.isCanceled;
  const isRejected = order.isRejected;

  const StatusIcon = order.isCanceled
    ? CancelIcon // ⛔ cancelada
    : order.isRejected
    ? BlockIcon // 🚫 rechazada
    : CheckCircleIcon; // ✅ flujo normal

  const statusLabel = isCanceled ? "Cancelada" : isRejected ? "Rechazada" : order.currentOrderProcessStep.name;

  const statusColor = isCanceled || isRejected ? "error" : "primary";

  const reason =
    isCanceled && order.cancelReason
      ? order.cancelReason
      : isRejected && order.rejectReason
      ? order.rejectReason
      : null;

  return (
    <>
      <Card
        sx={{
          m: 1,
          display: "flex",
          position: "relative",
          minWidth: 300,
          height: 200,
          cursor: "pointer",
        }}
        elevation={1}
        onClick={handleOpen}
      >
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            m: 2,
          }}
        >
          {/* ───── fecha ───── */}
          <Typography variant="subtitle2" color="primary" textAlign="left">
            {formatDateWithTime(order.updatedAt)}
          </Typography>

          {/* ───── estado actual ───── */}
          <Stack direction="row" alignItems="center" spacing={1} mt={1}>
            <Typography variant="subtitle2">Estado actual:</Typography>
            <Chip
              label={statusLabel}
              icon={<StatusIcon fontSize="small" />}
              color={statusColor as any}
              sx={{ fontWeight: 600 }}
            />
          </Stack>

          {/* ───── motivo de cancelación / rechazo ───── */}
          {reason && (
            <Typography variant="body2" color="error" mt={0.5} ml={0.5}>
              {reason}
            </Typography>
          )}

          {/* ───── detalles de la orden ───── */}
          <Box mt={1} flex={1} sx={{ overflowX: "hidden" }}>
            <Typography variant="subtitle2" mb={1} textAlign="center">
              Detalles de la orden
            </Typography>
            <Box ml={1} mr={1}>
              <ActiveOrderDetails details={order.details} />
            </Box>
          </Box>
        </Box>

        {/* ───── badge con ID ───── */}
        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            bgcolor: palette.primary.main,
            px: 2,
            py: 1,
            borderRadius: "0 0 0 10px",
          }}
        >
          <Typography color="white" fontWeight={600}>
            {order.id}
          </Typography>
        </Box>
      </Card>

      <SectorOrderModal order={order} handleClose={handleClose} isModalOpen={isModalOpen} />
    </>
  );
};
